import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Frame } from '@/types/event';
import { buildIndex, makeGetSurroundingFrames } from '@/utils/bbox-index';
import { getVideoElementFromRef } from '@/utils/video-player';
import { SourceType } from '@/types';
import type { PictureRect } from '@/types/video-player';
import { Bbox } from '@/components/Bbox';
import { VideoPlayerEventEmitter } from './VideoPlayerEventEmitter';

interface VideoBoundingBoxProps {
    sourceType?: SourceType;
    timeBase?: number;
    pictureRect: PictureRect | null;
    frames: Frame[];
    videoRef: React.RefObject<any>;
    emitter: VideoPlayerEventEmitter;
    intervalSeconds?: number;
}

interface Rectangle {
    left: number;
    top: number;
    width: number;
    height: number;
}

function interpolateBoundingBox(
    boundingBox1: [number, number, number, number],
    boundingBox2: [number, number, number, number],
    progress: number,
): [number, number, number, number] {
    const [x1a, y1a, x2a, y2a] = boundingBox1;
    const [x1b, y1b, x2b, y2b] = boundingBox2;

    return [
        x1a + (x1b - x1a) * progress,
        y1a + (y1b - y1a) * progress,
        x2a + (x2b - x2a) * progress,
        y2a + (y2b - y2a) * progress,
    ];
}

function boundingBoxToRectangle(
    boundingBox: [number, number, number, number] | null,
): Rectangle | null {
    if (!boundingBox) return null;
    const [x1, y1, x2, y2] = boundingBox;
    return { left: x1, top: y1, width: x2 - x1, height: y2 - y1 };
}

function scaleRectangle(
    rectangle: Rectangle | null,
    scaleX: number,
    scaleY: number,
    offsetX: number = 0,
    offsetY: number = 0,
): Rectangle | null {
    if (!rectangle) return null;
    return {
        left: rectangle.left * scaleX + offsetX,
        top: rectangle.top * scaleY + offsetY,
        width: rectangle.width * scaleX,
        height: rectangle.height * scaleY,
    };
}

function applyRectangle(
    element: HTMLDivElement | null,
    rectangle: Rectangle | null,
    durationMilliseconds: number,
) {
    if (!element) return;
    if (!rectangle) {
        element.style.opacity = '0';
        return;
    }

    element.style.opacity = '1';
    element.style.transitionDuration = `${Math.max(0, durationMilliseconds)}ms`;
    element.style.left = `${rectangle.left}px`;
    element.style.top = `${rectangle.top}px`;
    element.style.width = `${rectangle.width}px`;
    element.style.height = `${rectangle.height}px`;
}

const getBoundingBoxCurrentTime = (
    sourceType: SourceType = SourceType.FILE,
    timeBase: number = 0,
    currentTime: number = 0,
) => {
    if (sourceType === SourceType.CAMERA) {
        return timeBase + currentTime;
    }
    return currentTime;
};

const calculateBoundingBoxDurationMilliseconds = (
    currentTime: number = 0,
    nextTime: number,
) => {
    return (nextTime - currentTime) * 1000;
};

const BOX_HOLD_SECONDS = 0.3;

export const VideoBoundingBox: React.FC<VideoBoundingBoxProps> = ({
    sourceType,
    timeBase,
    pictureRect,
    frames,
    videoRef,
    emitter,
    intervalSeconds = 0.25,
}) => {
    const [videoReady, setVideoReady] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const scaleRef = useRef({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 });
    const suspendedRef = useRef(false);

    const index = useMemo(
        () => buildIndex(frames, intervalSeconds),
        [frames, intervalSeconds],
    );
    const getSurroundingFrames = useMemo(
        () => makeGetSurroundingFrames(index),
        [index],
    );

    useEffect(() => {
        const checkReady = () => {
            const videoElement = getVideoElementFromRef(videoRef);
            if (
                videoElement &&
                videoElement.videoWidth > 0 &&
                videoElement.videoHeight > 0
            ) {
                setVideoReady(true);
            }
        };

        const unsubscribeMetadata = emitter.on('loadedmetadata', checkReady);
        const unsubscribeData = emitter.on('loadeddata', checkReady);
        checkReady();
        return () => {
            unsubscribeMetadata();
            unsubscribeData();
        };
    }, [emitter, videoRef]);

    useEffect(() => {
        if (!pictureRect) return;
        scaleRef.current = {
            scaleX: pictureRect.scaleX,
            scaleY: pictureRect.scaleY,
            offsetX: pictureRect.offsetX,
            offsetY: pictureRect.offsetY,
        };
    }, [pictureRect]);

    useEffect(() => {
        const videoElement = getVideoElementFromRef(videoRef);
        if (!videoElement || frames.length === 0 || !videoReady) return;

        let requestAnimationFrameId = 0;

        const hide = () => {
            suspendedRef.current = true;
            if (boxRef.current) {
                boxRef.current.style.opacity = '0';
            }
        };

        const resume = () => {
            suspendedRef.current = false;
        };

        const unsubscribeSeeking = emitter.on('seeking', hide);
        const unsubscribeWaiting = emitter.on('waiting', hide);
        const unsubscribeSourceChange = emitter.on('sourcechange', hide);
        const unsubscribeSeeked = emitter.on('seeked', resume);
        const unsubscribeStart = emitter.on('start', resume);
        const unsubscribePlaying = emitter.on('playing', resume);

        const tick = () => {
            const box = boxRef.current;
            if (!box) {
                requestAnimationFrameId = requestAnimationFrame(tick);
                return;
            }

            if (suspendedRef.current) {
                box.style.opacity = '0';
                requestAnimationFrameId = requestAnimationFrame(tick);
                return;
            }

            const time = getBoundingBoxCurrentTime(
                sourceType,
                timeBase ?? 0,
                videoElement.currentTime,
            );

            const { current, next } = getSurroundingFrames(time);

            const hold = frames.length === 1 ? BOX_HOLD_SECONDS : 0;
            const isAtLastFrame = !!current && !!next && current.id === next.id;
            const beforeFirstFrame = !!current && time < current.time;
            const shouldHide =
                !current ||
                !next ||
                (beforeFirstFrame && time < current.time - hold) ||
                (isAtLastFrame && time > current.time + hold);

            if (shouldHide) {
                box.style.opacity = '0';
            } else {
                const span = next.time - current.time;
                const progress =
                    span > 0
                        ? Math.min(1, Math.max(0, (time - current.time) / span))
                        : 0;

                const interpolatedBoundingBox = interpolateBoundingBox(
                    current.bbox,
                    next.bbox,
                    progress,
                );

                const { scaleX, scaleY, offsetX, offsetY } = scaleRef.current;

                const interpolatedRectangle = scaleRectangle(
                    boundingBoxToRectangle(interpolatedBoundingBox),
                    scaleX,
                    scaleY,
                    offsetX,
                    offsetY,
                );

                box.style.transitionDuration = '0ms';
                applyRectangle(box, interpolatedRectangle, 0);
            }

            requestAnimationFrameId = requestAnimationFrame(tick);
        };

        requestAnimationFrameId = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(requestAnimationFrameId);
            unsubscribeSeeking();
            unsubscribeWaiting();
            unsubscribeSourceChange();
            unsubscribeSeeked();
            unsubscribeStart();
            unsubscribePlaying();
        };
    }, [getSurroundingFrames, videoRef, frames, videoReady, timeBase, emitter]);

    if (!videoReady || frames.length === 0) return null;

    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <Bbox
                ref={boxRef}
                className="opacity-0"
                style={{ willChange: 'left, top, width, height' }}
            />
        </div>
    );
};
